import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Group, Loader, Paper, Select, Stack, Table, Text } from '@mantine/core';
import { showToast } from '../../../../utils/toast';
import { useUsersDirectory } from '../../../../hooks/useUserApi';
import { GetPerformanceReviewRoundsRoundIdSummarizers200Item } from '../../../../api/generated/model/getPerformanceReviewRoundsRoundIdSummarizers200Item';
import {
  useGetPerformanceReviewRoundsRoundIdSummarizers,
  getGetPerformanceReviewRoundsRoundIdSummarizersQueryKey,
  usePutPerformanceReviewRoundsRoundIdSummarizersUserId,
  usePostPerformanceReviewRoundsRoundIdSummarizersRecompute,
  usePostPerformanceReviewRoundsRoundIdSummarizersCopyFromSourceRoundId,
} from '../../../../api/generated/performance-review/performance-review';
import { User } from '../../../../types';

interface SummarizersPanelProps {
  roundId: string;
  /** Прошлые раунды — источник для «скопировать назначения». */
  previousRounds: { _id: string; title: string }[];
}

export const SummarizersPanel: React.FC<SummarizersPanelProps> = ({ roundId, previousRounds }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [sourceRoundId, setSourceRoundId] = useState<string | null>(null);

  const { data, isLoading } = useGetPerformanceReviewRoundsRoundIdSummarizers(roundId);
  // Мутатор клиента возвращает уже распакованное тело ответа, а сгенерированный
  // тип обещает обёртку {data, status} — отсюда каст, как и в остальных вызовах.
  const rows = (data as unknown as GetPerformanceReviewRoundsRoundIdSummarizers200Item[]) || [];

  const { data: users = [] } = useUsersDirectory();

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: getGetPerformanceReviewRoundsRoundIdSummarizersQueryKey(roundId),
    });

  const onError = () =>
    showToast({
      title: t('common.error'),
      message: t('perfReview.summarizerSaveError'),
      color: 'red',
    });

  const assign = usePutPerformanceReviewRoundsRoundIdSummarizersUserId({
    mutation: { onSuccess: invalidate, onError },
  });
  const recompute = usePostPerformanceReviewRoundsRoundIdSummarizersRecompute({
    mutation: { onSuccess: invalidate, onError },
  });
  const copyFrom = usePostPerformanceReviewRoundsRoundIdSummarizersCopyFromSourceRoundId({
    mutation: { onSuccess: invalidate, onError },
  });

  const allUsers = users as User[];
  // Сам участник из списка убран: ответственный за себя увидел бы собственный
  // сырой фидбек, который анонимен.
  const optionsFor = (participantId: string) =>
    allUsers.filter((u) => u._id !== participantId).map((u) => ({ value: u._id, label: u.name }));

  if (isLoading) {
    return (
      <Group justify="center" p="xl">
        <Loader />
      </Group>
    );
  }

  return (
    <Stack gap="md">
      <Paper withBorder p="md">
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Button
            variant="light"
            loading={recompute.isPending}
            onClick={() => recompute.mutate({ roundId })}
          >
            {t('perfReview.summarizerRecompute')}
          </Button>

          <Group align="flex-end" gap="sm">
            <Select
              label={t('perfReview.summarizerCopy')}
              placeholder={t('perfReview.summarizerCopyPlaceholder')}
              data={previousRounds.map((r) => ({ value: r._id, label: r.title }))}
              value={sourceRoundId}
              onChange={setSourceRoundId}
              searchable
              w={260}
            />
            <Button
              variant="light"
              disabled={!sourceRoundId}
              loading={copyFrom.isPending}
              onClick={() => sourceRoundId && copyFrom.mutate({ roundId, sourceRoundId })}
            >
              {t('common.apply')}
            </Button>
          </Group>
        </Group>
      </Paper>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('perfReview.participant')}</Table.Th>
            <Table.Th>{t('perfReview.summarizer')}</Table.Th>
            <Table.Th>{t('perfReview.summarizerSourceLabel')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => {
            const userId = row.user?._id || '';
            return (
              <Table.Tr key={row._id || userId}>
                <Table.Td>
                  <Text size="sm">{row.user?.name}</Text>
                </Table.Td>
                <Table.Td>
                  <Select
                    aria-label={t('perfReview.summarizer')}
                    placeholder={t('perfReview.summarizerAssign')}
                    data={optionsFor(userId)}
                    value={row.summarizer?._id || null}
                    onChange={(value) =>
                      assign.mutate({
                        roundId,
                        userId,
                        data: { summarizer: value },
                      })
                    }
                    searchable
                    clearable
                    w={260}
                  />
                </Table.Td>
                <Table.Td>
                  {row.summarizer ? (
                    <Badge color="teal" variant="light">
                      {t(`perfReview.summarizerSource.${row.source || 'assigned'}`)}
                    </Badge>
                  ) : (
                    <Badge color="red" variant="light">
                      {t(`perfReview.summarizerReason.${row.reason || 'ambiguous'}`)}
                    </Badge>
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};
